import React, { useState, useEffect, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import axiosInstance from "../../../services/AxiosInstance";
import swal from "sweetalert";

const errMsg = (e) => e?.response?.data?.message || e.message || "Something went wrong";

const AutopilotSettings = ({ onChanged }) => {
  const [ai, setAi] = useState(null);
  const [provider, setProvider] = useState("anthropic");
  const [apiKey, setApiKey] = useState("");
  const [textModel, setTextModel] = useState("");
  const [busy, setBusy] = useState("");

  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState("");
  const [brand, setBrand] = useState(null);

  const loadAI = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get("autopilot/ai-settings");
      setAi(data.data); setProvider(data.data.provider); setTextModel(data.data.textModel || "");
    } catch (e) { swal("Error", errMsg(e), "error"); }
  }, []);

  useEffect(() => {
    loadAI();
    axiosInstance.get("channels").then(({ data }) => {
      const list = data.data || [];
      setChannels(list); setChannelId(list[0]?._id || "");
    }).catch(() => {});
  }, [loadAI]);

  useEffect(() => {
    if (!channelId) return;
    setBrand(null);
    axiosInstance.get(`autopilot/brand/${channelId}`).then(({ data }) => setBrand(data.data)).catch(() => {});
  }, [channelId]);

  const saveAI = async () => {
    setBusy("save");
    try {
      await axiosInstance.post("autopilot/ai-settings", { provider, apiKey, textModel });
      setApiKey(""); await loadAI(); onChanged && onChanged();
      swal("Saved", "AI settings saved.", "success");
    } catch (e) { swal("Error", errMsg(e), "error"); }
    setBusy("");
  };
  const testAI = async () => {
    setBusy("test");
    try { const { data } = await axiosInstance.post("autopilot/ai-settings/test"); swal("Works", data.message, "success"); }
    catch (e) { swal("Key test failed", errMsg(e), "error"); }
    setBusy("");
  };

  const setB = (k, v) => setBrand((b) => ({ ...b, [k]: v }));
  const saveBrand = async () => {
    setBusy("brand");
    try {
      await axiosInstance.post(`autopilot/brand/${channelId}`, {
        ...brand,
        bannedWords: typeof brand.bannedWords === "string" ? brand.bannedWords.split(",") : brand.bannedWords,
        examplePosts: brand.examplePosts,
      });
      swal("Saved", "Brand profile saved.", "success");
    } catch (e) { swal("Error", errMsg(e), "error"); }
    setBusy("");
  };
  const resetLearning = async () => {
    try { await axiosInstance.post(`autopilot/brand/${channelId}`, { resetLearning: true }); setB("rejectionCounts", {}); swal("Done", "Learning from rejections was reset.", "success"); }
    catch (e) { swal("Error", errMsg(e), "error"); }
  };

  const examples = brand ? [0, 1, 2].map((i) => (brand.examplePosts || [])[i] || "") : [];
  const setExample = (i, v) => { const arr = [...examples]; arr[i] = v; setB("examplePosts", arr); };
  const bannedStr = brand ? (Array.isArray(brand.bannedWords) ? brand.bannedWords.join(", ") : brand.bannedWords || "") : "";
  const rejections = brand ? Object.entries(brand.rejectionCounts || {}) : [];

  return (
    <div className="row">
      <div className="col-lg-6">
        <div className="card">
          <div className="card-header"><h5 className="card-title mb-0">AI connection</h5></div>
          <div className="card-body">
            {!ai ? <Spinner animation="border" variant="primary" /> : (
              <>
                <p className="text-muted small">Autopilot uses <strong>your own</strong> AI account. Your key is stored encrypted and is never shown again.</p>
                <div className="form-group">
                  <label className="font-w500">Provider</label>
                  <select className="form-control" value={provider} onChange={(e) => { setProvider(e.target.value); setTextModel(""); }}>
                    <option value="anthropic">Anthropic (Claude)</option>
                    <option value="openai">OpenAI (GPT)</option>
                    <option value="gemini">Google Gemini</option>
                  </select>
                </div>
                {ai.hasKey && provider !== ai.provider && <div className="alert alert-warning py-2 small">Changing the provider removes the saved key. Enter a key for the new provider below.</div>}
                <div className="form-group">
                  <label className="font-w500">API key {ai.hasKey && <span className="badge badge-success ml-2">Saved {ai.keyMask}</span>}</label>
                  <input type="password" autoComplete="off" className="form-control" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                    placeholder={ai.hasKey ? "Leave empty to keep the saved key" : provider === "openai" ? "sk-..." : provider === "gemini" ? "AIza..." : "sk-ant-..."} />
                </div>
                <div className="form-group">
                  <label className="font-w500">Model (optional)</label>
                  <input className="form-control" value={textModel} onChange={(e) => setTextModel(e.target.value)} placeholder={ai.defaultModels[provider]} />
                </div>
                <div className="d-flex gap-2">
                  <button className="btn btn-primary btn-sm" disabled={busy === "save"} onClick={saveAI}>{busy === "save" ? <Spinner animation="border" size="sm" /> : "Save"}</button>
                  <button className="btn btn-outline-primary btn-sm" disabled={!ai.hasKey || busy === "test"} onClick={testAI}>{busy === "test" ? <Spinner animation="border" size="sm" /> : "Test key"}</button>
                </div>
                <div className="text-muted small mt-3">AI posts drafted this month: <strong>{ai.usageCount}</strong></div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="col-lg-6">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">Style for channel</h5>
            <select className="form-control form-control-sm" style={{ maxWidth: 200 }} value={channelId} onChange={(e) => setChannelId(e.target.value)}>
              {channels.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
          <div className="card-body">
            {!channels.length ? <p className="text-muted">Add a channel first.</p> : !brand ? <Spinner animation="border" variant="primary" /> : (
              <>
                <div className="row">
                  <div className="col-6 form-group"><label className="font-w500">Language</label><input className="form-control" value={brand.language} onChange={(e) => setB("language", e.target.value)} /></div>
                  <div className="col-6 form-group"><label className="font-w500">Tone</label>
                    <select className="form-control" value={brand.tone} onChange={(e) => setB("tone", e.target.value)}>
                      <option value="friendly">Friendly</option><option value="news">News</option><option value="professional">Professional</option><option value="funny">Funny</option><option value="custom">Custom</option>
                    </select></div>
                </div>
                {brand.tone === "custom" && <div className="form-group"><label className="font-w500">Describe the tone</label><input className="form-control" value={brand.customTone} onChange={(e) => setB("customTone", e.target.value)} /></div>}
                <div className="row">
                  <div className="col-6 form-group"><label className="font-w500">Max length (characters)</label><input type="number" className="form-control" value={brand.maxLength} onChange={(e) => setB("maxLength", e.target.value)} /></div>
                  <div className="col-6 form-group pt-4">
                    <div className="form-check"><input id="em" type="checkbox" className="form-check-input" checked={brand.useEmoji} onChange={(e) => setB("useEmoji", e.target.checked)} /><label htmlFor="em" className="form-check-label">Use emojis</label></div>
                    <div className="form-check"><input id="sl" type="checkbox" className="form-check-input" checked={brand.addSourceLink} onChange={(e) => setB("addSourceLink", e.target.checked)} /><label htmlFor="sl" className="form-check-label">Add source link</label></div>
                  </div>
                </div>
                <div className="form-group"><label className="font-w500">Signature (added to every post)</label><input className="form-control" value={brand.signature} onChange={(e) => setB("signature", e.target.value)} placeholder="Follow us for daily updates" /></div>
                <div className="form-group"><label className="font-w500">Banned words (comma separated)</label><input className="form-control" value={bannedStr} onChange={(e) => setB("bannedWords", e.target.value)} /></div>
                <div className="form-group"><label className="font-w500">Topics to avoid</label><input className="form-control" value={brand.avoidTopics} onChange={(e) => setB("avoidTopics", e.target.value)} placeholder="politics, gambling" /></div>
                <div className="form-group"><label className="font-w500">Example posts in your voice (up to 3)</label>
                  {examples.map((x, i) => <textarea key={i} rows={2} className="form-control mb-2" value={x} onChange={(e) => setExample(i, e.target.value)} placeholder={`Example ${i + 1}`} />)}
                </div>
                {rejections.length > 0 && (
                  <div className="small text-muted mb-3">
                    What the AI learned from your rejections: {rejections.map(([k, n]) => `${k} (${n})`).join(", ")}{" "}
                    <button className="btn btn-link btn-sm p-0" onClick={resetLearning}>Reset</button>
                  </div>
                )}
                <button className="btn btn-primary btn-sm" disabled={busy === "brand"} onClick={saveBrand}>{busy === "brand" ? <Spinner animation="border" size="sm" /> : "Save style"}</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutopilotSettings;
